/**
 * Landing Page template
 * This file contains logic specific to a template.
 * It is intended to ONLY be loaded on templates that require
 * this specific functionality.
 **/

import Global from './lib/global';
import Logger from './lib/logger';
import LandingSlider from './landing-page-carousel';
import 'keen-slider/keen-slider.min.css'
import data from './data/explore-counties-2024-09-09';

const fuzzysort = require('fuzzysort');

// Vars
// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

const NAME = 'Landing Page';
const changeCountyDataAttribute = 'data-update-page-for-county';

// Research Reports 'View More'
const researchReportCards = document.querySelectorAll('[data-research-report-card]');
const viewMoreResearchReportButton = document.querySelector('[data-view-more-research-report-button]');
const viewMoreResearchReportButtonHideClass = 'hidden';

// County Search Fields
const searchInputField = document.querySelector('[data-county-search-input]');
const searchResultsContainer = document.querySelector('[data-county-search-results]');
const noResultsContainer = document.querySelector('[data-search-no-results]');

// Logic
// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

// Bootstrap global functionality
Global();

Logger.info(`${NAME} > page was loaded.`);

/**
 * init --- Initialize the view
 */
const init = () => {
	Logger.info(`${NAME} > init`);

	if (researchReportCards && viewMoreResearchReportButton) {
		initMoreResearchReport();
	}

	if (searchInputField) {
		initSearch();
	}
}

/**
* initMoreResearchReport --- Initialize the view more recent press button
*/
const initMoreResearchReport = () => {
	Logger.info(`${NAME} > initMoreResearchReport`);

	viewMoreResearchReportButton.addEventListener('click', (e) => {
		e.preventDefault();
		researchReportCards.forEach((item) => {
			item.classList.remove(viewMoreResearchReportButtonHideClass);
		});

		viewMoreResearchReportButton.classList.add(viewMoreResearchReportButtonHideClass);
	});
}

/**
 * Run a fuzzy string search for a county name.
 **/
const searchForCountyByName = async (searchStr) => {
	console.time('searchForCountyByName');
	const results = await fuzzysort.goAsync(searchStr, data, {
		key: 'county_state',
		limit: 50
	});
	console.timeEnd('searchForCountyByName');
	return results;
}

/**
 * Initialize the search field.
 **/
const initSearch = () => {
	Logger.info(`Initializing search...`);

	// Invalid keys
	const invalidKeys = [
		13, // enter
		37, // arrow left
		38, // arrow up
		39, // arrow right
		40, // arrow down
	];

	searchInputField.addEventListener('keyup', async (e) => {

		// Handle keyCodes that are not alphanumeric
		// (These might be useful for helping the user
		// navigate the results dropdown).
		if (invalidKeys.includes(e.keyCode)) {
			Logger.info('Omitting keyup due to invalid keycode.');
			return;
		}

		const str = searchInputField.value;
		const searchResults = await searchForCountyByName(str);

		if (!searchResults.length) {
			if (str !== '') {
				noResultsContainer.querySelector('[data-search-no-result-preview]').textContent = str;
				noResultsContainer.setAttribute('data-search-no-results-active', null);
			}
			searchResultsContainer.innerHTML = '';
			searchResultsContainer.parentElement.classList.remove('show');
		} else {
			noResultsContainer.removeAttribute('data-search-no-results-active');
			//
			const firstCounty = searchResults[0].target.split(',')[0];
			const matchingCounties = searchResults.filter(r => r.target.split(',')[0] === firstCounty);
			const remainingConties = searchResults.filter(r => r.target.split(',')[0] !== firstCounty);
			matchingCounties.sort((a, b) => (a.target.split(', ')[1] > b.target.split(', ')[1]) ? 1 : -1);
			/* console.log(firstCounty, matchingCounties, remainingConties); */
			// Hydrate the results
			const htmlParts = [...matchingCounties, ...remainingConties].map((result) => {
				return `<li class="explore-the-data__search-result" data-update-page-for-county="${result.obj.cfips}">${result.obj.county_state}</li>`;
			});
			searchResultsContainer.innerHTML = htmlParts.join('');
			searchResultsContainer.parentElement.classList.add('show');

			// Listen for click targets
			const clickTargets = searchResultsContainer.querySelectorAll(`[${changeCountyDataAttribute}]`);
			clickTargets.forEach((item) => {
				item.addEventListener('click', (e) => {
					e.preventDefault();
					searchInputField.value = e.target.textContent;
					searchResultsContainer.parentElement.classList.remove('show');

					const cfips = item.getAttribute(changeCountyDataAttribute);
					if (!cfips) {
						Logger.error(`Target does not have a cfips value.`);
						return;
					}

					// Record the analytics event
					const eid = `comms.microsites.venture-forward/${window.data_vars.eid_page_slug}.search-selection.selected`;
					_expDataLayer.push({
						'schema': 'add_event', 'version': 'v2', 'data': {
							'eid': eid,
							'type': 'click',
							'custom_properties': {
								'search_value': searchInputField.value
							}
						}
					});
					Logger.info(`Triggering event: ${eid} with search_value ${searchInputField.value}`);

					window.location.href = '/ventureforward/explore-the-data/?cfips=' + cfips;
				});
			});
		}
	});

	Logger.info(`Search initialized.`);
}

init();

// Do not edit below this line...
// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒
Logger.info(`${NAME} > loaded`);
